import { useState } from "react";
import { api } from "../api/client";
import { useData } from "../hooks/useData";
import { I } from "./Icons";
import { clsx } from "./Primitives";
import type { Partner } from "../types";

type Level = "none" | "read" | "write" | "send";
interface AgentRow {
  id: string; name: string; description: string | null; acts_as: string; acts_as_name: string | null; scope: "own" | "all";
  permissions: Record<string, Level>; email_signature: string | null; test_mode: boolean; webhooks: number; key_prefix: string; active: boolean; created_at: string; last_used_at: string | null;
  use_count: number; changes: number;
}
interface Meta { areas: { id: string; label: string }[]; guide: string; agents: AgentRow[] }
interface Activity {
  action: string; ok: boolean; entity_type: string | null; summary: string | null; error: string | null; created_at: string;
  action_id: string | null; revertible: boolean | null; reverted_at: string | null; request_id: string | null;
}
interface Webhook { id: string; url: string; events: string[]; active: boolean; last_delivered_at: string | null; last_error: string | null; consecutive_failures: number; pending: number }
interface Form { name: string; description: string; acts_as: string; scope: "own" | "all"; permissions: Record<string, Level>; email_signature: string; test_mode: boolean }

const LEVELS: [Level, string][] = [["none", "No access"], ["read", "Read"], ["write", "Read & write"], ["send", "+ Send"]];
// Emails: write = file under a project / category, dismiss; send = also send on the partner's behalf.
const levelLabel = (area: string, lv: Level, label: string) =>
  area !== "emails" ? label : lv === "write" ? "Read & file" : lv === "send" ? "File & send" : label;
const chipLabel = (area: string, lv: Level) => (lv === "read" ? " · read" : area === "emails" && lv === "write" ? " · file" : area === "emails" && lv === "send" ? " · file & send" : "");
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "never");

export default function Agents({ partners, me }: { partners: Record<string, Partner>; me: string }) {
  const { data, refetch } = useData<Meta>("/api/admin/agents");
  const [editing, setEditing] = useState<AgentRow | "new" | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [shownKey, setShownKey] = useState<{ name: string; key: string; actsAs: string } | null>(null);
  const [activityFor, setActivityFor] = useState<AgentRow | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const areas = data?.areas || [];
  const base = `${window.location.origin}/api/agent/v1`;

  const preset = (kind: "assistant" | "readonly"): Record<string, Level> =>
    Object.fromEntries(areas.map(a => [a.id, kind === "readonly" ? "read" : a.id === "leads" ? "read" : a.id === "emails" ? "send" : "write"]));
  const open = (a: AgentRow | "new") => {
    setErr(null); setEditing(a); setShownKey(null);
    setForm(a === "new"
      ? { name: "", description: "", acts_as: me, scope: "own", permissions: preset("assistant"), email_signature: "", test_mode: false }
      : { name: a.name, description: a.description || "", acts_as: a.acts_as, scope: a.scope, permissions: { ...a.permissions }, email_signature: a.email_signature || "", test_mode: !!a.test_mode });
  };
  const save = async () => {
    if (!form) return;
    setErr(null);
    try {
      if (editing === "new") {
        const r = await api.post<{ key: string }>("/api/admin/agents", form);
        setShownKey({ name: form.name, key: r.key, actsAs: partners[form.acts_as]?.name || form.acts_as });
      } else if (editing) {
        await api.put(`/api/admin/agents/${editing.id}`, form);
      }
      setEditing(null); setForm(null); refetch();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  const rotate = async (a: AgentRow) => {
    if (!confirm(`Make a new key for ${a.name}? The old key stops working immediately.`)) return;
    const r = await api.post<{ key: string }>(`/api/admin/agents/${a.id}/rotate`, {});
    setShownKey({ name: a.name, key: r.key, actsAs: a.acts_as_name || a.acts_as }); refetch();
  };
  const toggle = async (a: AgentRow) => {
    await api.put(`/api/admin/agents/${a.id}`, { name: a.name, description: a.description, acts_as: a.acts_as, scope: a.scope, permissions: a.permissions, email_signature: a.email_signature, test_mode: a.test_mode, active: !a.active });
    refetch();
  };
  const remove = async (a: AgentRow) => {
    if (!confirm(`Delete ${a.name}? Its key stops working. What it already recorded stays in the CRM.`)) return;
    await api.delete(`/api/admin/agents/${a.id}`); refetch();
  };

  return (
    <>
      <div className="card set-card">
        <div className="set-card-head">
          <div>
            <div className="section-label">Bots & AI agents</div>
            <div className="set-hint">
              Give a bot — like your personal Grok agent — its own key. It acts on behalf of one partner, sees only that partner's
              records or everyone's, and gets read or write access per area. Everything it does is logged here and credited to that partner.
            </div>
          </div>
          <button className="btn primary sm" onClick={() => open("new")}>{I.plus({ size: 13, w: 2 })}New bot</button>
        </div>
        {err && !form && <div className="set-notice err">{err}</div>}
        {shownKey && <KeyPanel k={shownKey} base={base} guide={data?.guide || ""} onDone={() => setShownKey(null)} />}

        {form && (
          <div className="set-form">
            <div className="set-grid">
              <label className="set-field"><span className="set-label">Name</span>
                <input autoFocus value={form.name} placeholder="e.g. Geir's Grok agent" onChange={e => setForm({ ...form, name: e.target.value })} /></label>
              <label className="set-field"><span className="set-label">Acts on behalf of</span>
                <select value={form.acts_as} onChange={e => setForm({ ...form, acts_as: e.target.value })}>
                  {Object.values(partners).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></label>
              <label className="set-field wide"><span className="set-label">What it's for (optional)</span>
                <input value={form.description} placeholder="e.g. I paste meeting notes and emails into Grok on my phone" onChange={e => setForm({ ...form, description: e.target.value })} /></label>
              <div className="set-field wide">
                <span className="set-label">Whose records</span>
                <label className="set-check"><input type="radio" checked={form.scope === "own"} onChange={() => setForm({ ...form, scope: "own" })} />
                  <span><strong>Only {partners[form.acts_as]?.name.split(" ")[0] || "their"}'s</strong> — their mailbox, and the deals and relationship targets they own (a personal agent)</span></label>
                <label className="set-check"><input type="radio" checked={form.scope === "all"} onChange={() => setForm({ ...form, scope: "all" })} />
                  <span><strong>All partners'</strong> — e.g. a shared team bot</span></label>
                <span className="set-hint">Tasks, accounts, contacts, minutes, saved intel and leads are shared by everyone — like a user, the bot can give tasks to colleagues and change or reassign anyone's. For those, the access below applies.</span>
              </div>
              <div className="set-field wide">
                <span className="set-label">Access per area
                  <span className="ag-presets">
                    <button type="button" className="ld-link" onClick={() => setForm({ ...form, permissions: preset("assistant") })}>Personal assistant</button>
                    <button type="button" className="ld-link" onClick={() => setForm({ ...form, permissions: preset("readonly") })}>Read-only</button>
                  </span>
                </span>
                <table className="ag-perms">
                  <tbody>
                    {areas.map(a => (
                      <tr key={a.id}>
                        <td>{a.label}</td>
                        {LEVELS.map(([lv, label]) => (
                          <td key={lv}>
                            {(lv !== "send" || a.id === "emails") && (
                              <label className={clsx("ag-lv", form.permissions[a.id] === lv && "on", lv)}>
                                <input type="radio" name={`p-${a.id}`} checked={(form.permissions[a.id] || "none") === lv}
                                  onChange={() => setForm({ ...form, permissions: { ...form.permissions, [a.id]: lv } })} />
                                {levelLabel(a.id, lv, label)}
                              </label>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <span className="set-hint">
                  Emails: <strong>Read</strong> sees the CRM inbox (only emails that passed the junk filter, never ones kept private) ·{" "}
                  <strong>Read & file</strong> also files them under a project and category, or dismisses them ·{" "}
                  <strong>File & send</strong> also sends and replies from {partners[form.acts_as]?.name.split(" ")[0] || "their"}'s Gmail.
                </span>
              </div>
              {form.permissions.emails === "send" && (
                <label className="set-field wide"><span className="set-label">Every email it sends ends with</span>
                  <input value={form.email_signature} maxLength={200}
                    placeholder={`Sent by the Personal Assistant for ${partners[form.acts_as]?.name || "…"}`}
                    onChange={e => setForm({ ...form, email_signature: e.target.value })} />
                  <span className="set-hint">Recipients also see the sender as “{partners[form.acts_as]?.name || "…"} (Personal Assistant)”. Leave empty for the default line.</span>
                </label>
              )}
              <label className="set-check wide">
                <input type="checkbox" checked={form.test_mode} onChange={e => setForm({ ...form, test_mode: e.target.checked })} />
                <span><strong>Test key</strong> — every action runs as a dry run, so nothing is ever saved or sent. Use one to try new actions after a deploy.</span>
              </label>
            </div>
            {err && <div className="set-notice err" style={{ margin: "10px 0 0" }}>{err}</div>}
            <div className="set-form-foot">
              <button className="btn ghost sm" onClick={() => { setEditing(null); setForm(null); }}>Cancel</button>
              <button className="btn primary sm" disabled={!form.name.trim()} onClick={save}>{editing === "new" ? "Create bot" : "Save"}</button>
            </div>
          </div>
        )}

        <div className="set-rows">
          {(data?.agents || []).map(a => (
            <div key={a.id} className={clsx("set-row", !a.active && "muted")}>
              <span className="set-avatar" style={{ background: "var(--ember)" }}>{I.ai({ size: 14 })}</span>
              <div className="set-row-main">
                <div className="set-row-title">
                  {a.name}
                  <span className="set-badge">{a.scope === "own" ? `${(a.acts_as_name || a.acts_as).split(" ")[0]}'s records` : "All partners"}</span>
                  {!a.active && <span className="set-badge">Paused</span>}
                  {a.test_mode && <span className="set-badge test">Test key — never saves</span>}
                  {a.webhooks > 0 && <span className="set-badge">{a.webhooks} webhook{a.webhooks === 1 ? "" : "s"}</span>}
                </div>
                <div className="set-row-sub">
                  Acts as {a.acts_as_name || a.acts_as} · <code>{a.key_prefix}…</code> · last used {when(a.last_used_at)} · {a.changes} change{a.changes === 1 ? "" : "s"}
                </div>
                <div className="ag-chips">
                  {areas.map(x => {
                    const lv = a.permissions[x.id] || "none";
                    return lv === "none" ? null : <span key={x.id} className={clsx("ag-chip", lv)}>{x.label}{chipLabel(x.id, lv)}</span>;
                  })}
                </div>
                {a.description && <div className="set-row-desc">{a.description}</div>}
              </div>
              <div className="set-row-actions">
                <button className="btn ghost sm" onClick={() => setActivityFor(activityFor?.id === a.id ? null : a)}>Activity</button>
                <button className="btn ghost sm" onClick={() => open(a)}>Edit</button>
                <button className="btn ghost sm" onClick={() => rotate(a)}>New key</button>
                <button className="btn ghost sm" onClick={() => toggle(a)}>{a.active ? "Pause" : "Resume"}</button>
                <button className="icon-btn nl-del" title="Delete" onClick={() => remove(a)}>{I.trash({ size: 14 })}</button>
              </div>
            </div>
          ))}
          {data && data.agents.length === 0 && !form && <div className="set-empty">No bots yet. Create one for your personal AI agent.</div>}
        </div>
      </div>
      {activityFor && <ActivityLog agent={activityFor} onClose={() => setActivityFor(null)} />}
    </>
  );
}

function ActivityLog({ agent, onClose }: { agent: AgentRow; onClose: () => void }) {
  const { data, refetch } = useData<Activity[]>(`/api/admin/agents/${agent.id}/activity`);
  const { data: hooks, refetch: refetchHooks } = useData<Webhook[]>(`/api/admin/agents/${agent.id}/webhooks`);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const counts = new Map<string, number>();
  (data || []).forEach(x => { if (x.request_id && x.ok && x.revertible && !x.reverted_at) counts.set(x.request_id, (counts.get(x.request_id) || 0) + 1); });
  const revert = async (x: Activity, whole: boolean, force = false) => {
    const n = whole && x.request_id ? counts.get(x.request_id) || 1 : 1;
    if (!force && !confirm(whole && n > 1 ? `Revert all ${n} changes from this request?` : `Revert “${x.summary || x.action}”?`)) return;
    try {
      const r = await api.post<{ reverted: unknown[] }>(`/api/admin/agents/actions/${x.action_id}/revert`, { whole_request: whole, force });
      setMsg({ kind: "ok", text: `Reverted ${r.reverted.length} change${r.reverted.length === 1 ? "" : "s"}` });
      refetch();
    } catch (e) {
      const text = e instanceof Error ? e.message : String(e);
      if (/changed again/.test(text) && confirm(`${text}\n\nRevert anyway and overwrite those later changes?`)) return revert(x, whole, true);
      setMsg({ kind: "err", text });
    }
  };
  return (
    <div className="card set-card">
      <div className="set-card-head">
        <div className="section-label">Activity — {agent.name}</div>
        <button className="btn ghost sm" onClick={onClose}>Close</button>
      </div>
      {msg && <div className={clsx("set-notice", msg.kind)}>{msg.text}</div>}
      <div className="set-rows">
        {(data || []).map((x, i) => (
          <div key={i} className={clsx("ag-act", x.reverted_at && x.action !== "revert" && "reverted")}>
            <span className={clsx("ag-dot", x.ok ? "ok" : "bad")} />
            <span className="ag-act-what">{x.ok ? x.summary || x.action : `${x.action} refused — ${x.error}`}{x.reverted_at && <em> · reverted</em>}</span>
            {x.ok && x.action_id && x.revertible && !x.reverted_at && x.action !== "revert" && (
              <span className="ag-act-rev">
                <button className="ld-link" onClick={() => revert(x, false)}>Revert</button>
                {x.request_id && (counts.get(x.request_id) || 0) > 1 && (
                  <button className="ld-link" onClick={() => revert(x, true)}>Revert all {counts.get(x.request_id)}</button>
                )}
              </span>
            )}
            <span className="ld-when">{when(x.created_at)}</span>
          </div>
        ))}
        {data && data.length === 0 && <div className="set-empty">Nothing yet.</div>}
      </div>
      {(hooks || []).length > 0 && (
        <>
          <div className="set-card-head" style={{ marginTop: 10 }}><div className="section-label">Webhooks</div></div>
          <div className="set-rows">
            {(hooks || []).map(h => (
              <div key={h.id} className="ag-act">
                <span className={clsx("ag-dot", h.active && !h.last_error ? "ok" : "bad")} />
                <span className="ag-act-what"><code>{h.url}</code> · {h.events.join(", ")}
                  {h.last_error && <em> · last error: {h.last_error}{h.consecutive_failures > 1 ? ` (${h.consecutive_failures}×)` : ""}</em>}
                  {!h.active && <em> · switched off after repeated failures</em>}
                  {h.pending > 0 && <em> · {h.pending} waiting</em>}</span>
                <span className="ld-when">{h.last_delivered_at ? `delivered ${when(h.last_delivered_at)}` : "nothing delivered yet"}</span>
                <button className="icon-btn nl-del" title="Delete webhook" onClick={async () => {
                  if (!confirm(`Delete the webhook to ${h.url}?`)) return;
                  await api.delete(`/api/admin/agents/webhooks/${h.id}`); refetchHooks();
                }}>{I.trash({ size: 13 })}</button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function KeyPanel({ k, base, guide, onDone }: { k: { name: string; key: string; actsAs: string }; base: string; guide: string; onDone: () => void }) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (what: string, text: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(what); setTimeout(() => setCopied(null), 1500); } catch { /* blocked */ }
  };
  const prompt = `You are ${k.actsAs}'s assistant for the Saga CRM (Saga Advisory — Norwegian energy technology for African operators, NOCs and governments).
When I paste or tell you anything worth keeping — meeting notes, an email, a business card, a call summary, news about a company or market, a new opportunity, a follow-up — record it in the CRM through its API, then tell me in one or two lines what was recorded.

API: ${base}
Every request: header "Authorization: Bearer ${k.key}" and "Content-Type: application/json".

1. Easiest — let the CRM work it out: POST ${base}/ingest  body {"text": "<what I pasted>", "note": "<optional hint, e.g. 'this was a call with NNPC today'>"}
   It returns {"summary", "results": [{"ok", "summary" | "error"}]}. Report the summaries; mention anything that failed and why.
   If I say "preview" or "don't save yet", add "dry_run": true and show me what would be recorded first.
2. When you need to look something up first: GET ${base}/search?q=<words>  or  GET ${base}/context (what you may see: deals, accounts, contacts, tasks, targets).
3. When I give you precise instructions, you may send exact changes: POST ${base}/actions  body {"actions": [ ... ], "dry_run": false}
${guide}

My emails (if this bot has email access):
- GET ${base}/emails?folder=review  — emails waiting to be filed (folder can also be all, routed or sent; add &q=words, &deal=name, &category=…, &weeks=8).
  GET ${base}/emails/<id>  — the full email, the thread, attachments, and the project the CRM's AI suggests.
- When I ask you to go through my inbox: for each email say in one line who it's from, what it's about and where you'd file it (project + category). File with email.file once I agree, or straight away if I said "file them". Use email.dismiss for anything not relevant to the CRM. Offer follow-ups (task.create, deal.note, contact.create) when an email calls for them.
- Sending (email.send, with reply_to = the email id when replying): ALWAYS show me the full draft — to, cc, subject, text — and wait for my explicit "send" in this conversation. Never send on your own initiative. Do a dry run first: the result's "preview" is exactly what will go out, ending with the line saying it was sent by my assistant. Don't add your own signature.

Safety (API v2): give every action an "idempotency_key" (e.g. a UUID, or the id of my message plus a counter) so a retry never creates it twice.
Every result has an "action_id"; if you made a mistake, POST ${base}/actions/<action_id>/revert (or /requests/<request_id>/revert for a whole batch) and tell me.
To catch up instead of re-reading everything: GET ${base}/changes?after=<cursor> — store "next" and pass it next time. To check if something exists: GET ${base}/contacts?match=<name>.
Missing an email address? GET ${base}/contacts/lookup-email?name=<full name>&account=<company>. If GET ${base}/me shows a new api_version, read its changelog before acting.

Rules: never invent facts, emails, numbers or dates. If a request is refused, tell me the reason the CRM gave — don't try to work around it. Keep the key secret; never show it to anyone.`;
  return (
    <div className="set-form ak-created">
      <div className="set-notice ok" style={{ margin: "0 0 12px" }}>Key for <strong>{k.name}</strong>. Copy it now — only a fingerprint is stored, so it can't be shown again.</div>
      <div className="ak-key"><code>{k.key}</code><button className="btn ghost sm" onClick={() => copy("key", k.key)}>{copied === "key" ? "Copied" : "Copy"}</button></div>
      <div className="set-hint" style={{ margin: "10px 0 6px" }}>
        Paste the instructions below into your bot (Grok, ChatGPT, Claude, a Telegram bot…) as its system prompt or custom instructions.
        The bot must be able to make web requests (an "HTTP / API tool" or "actions"). Full reference: <code>docs/AGENT_API.md</code>.
      </div>
      <div className="ak-snip">
        <div className="ak-snip-head"><span>Instructions for the bot (includes the key)</span>
          <button className="btn ghost sm" onClick={() => copy("prompt", prompt)}>{copied === "prompt" ? "Copied" : "Copy"}</button></div>
        <pre style={{ whiteSpace: "pre-wrap" }}>{prompt}</pre>
      </div>
      <div className="set-form-foot"><button className="btn primary sm" onClick={onDone}>I've saved the key</button></div>
    </div>
  );
}
